import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Bell, AlertTriangle, CheckCircle, X, Clock, 
  Building2, Users, ChevronRight, RefreshCw
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

import API from '@/config/api';
// API imported from config;

const NotificationBell = () => {
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const dropdownRef = useRef(null);

  const isAdmin = user?.role === 'admin';
  const canViewAlerts = user?.role === 'admin' || user?.role === 'provider';

  useEffect(() => {
    if (canViewAlerts) {
      fetchUnreadCount();
      // Poll for new alerts every 30 seconds
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [canViewAlerts]);

  useEffect(() => {
    if (isOpen && canViewAlerts) {
      fetchAlerts();
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/alerts/unread-count`, getAuthHeaders());
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Failed to fetch unread count');
    }
  };

  const fetchAlerts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/alerts`, getAuthHeaders());
      setAlerts(response.data);
    } catch (error) {
      console.error('Failed to fetch alerts');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (alertId) => {
    try {
      await axios.put(`${API}/alerts/${alertId}/read`, {}, getAuthHeaders());
      setAlerts(alerts.map(a => a.id === alertId ? { ...a, is_read: true } : a));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (error) {
      toast.error('Failed to mark alert as read');
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/alerts/mark-all-read`, {}, getAuthHeaders());
      setAlerts(alerts.map(a => ({ ...a, is_read: true })));
      setUnreadCount(0);
      toast.success('All alerts marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const resolveAlert = async (alertId) => {
    try {
      await axios.put(`${API}/alerts/${alertId}/resolve`, {}, getAuthHeaders());
      setAlerts(alerts.filter(a => a.id !== alertId));
      setUnreadCount(Math.max(0, unreadCount - 1));
      toast.success('Alert resolved');
    } catch (error) {
      toast.error('Failed to resolve alert');
    }
  };

  const checkRatios = async () => {
    setChecking(true);
    try {
      const response = await axios.post(`${API}/alerts/check-ratios`, {}, getAuthHeaders());
      toast.success(response.data.message);
      fetchAlerts();
      fetchUnreadCount();
    } catch (error) {
      toast.error('Failed to check ratios');
    } finally {
      setChecking(false);
    }
  };

  const getSeverityStyles = (severity) => {
    switch (severity) {
      case 'critical':
        return 'border-l-red-500 bg-red-50';
      case 'warning':
        return 'border-l-amber-500 bg-amber-50';
      default:
        return 'border-l-blue-500 bg-blue-50';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
        return <AlertTriangle className="w-4 h-4 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-4 h-4 text-amber-500" />;
      default:
        return <Bell className="w-4 h-4 text-blue-500" />;
    }
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (!canViewAlerts) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <Button
        variant="ghost"
        size="icon"
        className="relative"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="notification-bell"
      >
        <Bell className={cn(
          "w-5 h-5 transition-colors",
          unreadCount > 0 ? "text-primary" : "text-muted-foreground"
        )} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-medium animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-xl shadow-xl border border-border z-50 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b bg-muted/30 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-primary" />
              <span className="font-semibold">Ratio Alerts</span>
              {unreadCount > 0 && (
                <Badge variant="destructive" className="text-xs">
                  {unreadCount} new
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1">
              {isAdmin && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={checkRatios}
                  disabled={checking}
                  className="h-7 text-xs"
                  data-testid="check-ratios-btn"
                >
                  <RefreshCw className={cn("w-3 h-3 mr-1", checking && "animate-spin")} />
                  Check Now
                </Button>
              )}
              {alerts.some(a => !a.is_read) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={markAllRead}
                  className="h-7 text-xs"
                >
                  Mark all read
                </Button>
              )}
            </div>
          </div>

          {/* Alert List */}
          <ScrollArea className="max-h-[400px]">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary mx-auto"></div>
              </div>
            ) : alerts.length > 0 ? (
              <div className="divide-y">
                {alerts.map((alert) => (
                  <div
                    key={alert.id}
                    className={cn(
                      "p-3 border-l-4 transition-colors hover:bg-muted/20",
                      getSeverityStyles(alert.severity),
                      !alert.is_read && "bg-opacity-100"
                    )}
                    data-testid={`alert-${alert.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5">
                        {getSeverityIcon(alert.severity)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Building2 className="w-3 h-3 text-muted-foreground" />
                          <span className="font-medium text-sm truncate">
                            {alert.classroom_name}
                          </span>
                          {!alert.is_read && (
                            <span className="w-2 h-2 bg-primary rounded-full"></span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">
                          {alert.message}
                        </p>
                        {alert.data?.action_url && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs mb-2"
                            onClick={() => {
                              markAsRead(alert.id);
                              setIsOpen(false);
                              navigate(alert.data.action_url);
                            }}
                            data-testid={`open-alert-${alert.id}`}
                          >
                            Open
                            <ChevronRight className="w-3 h-3 ml-1" />
                          </Button>
                        )}
                        {alert.details && (
                          <div className="flex flex-wrap gap-2 text-xs mb-2">
                            <span className="px-2 py-0.5 bg-white rounded-full border">
                              <Users className="w-3 h-3 inline mr-1" />
                              {alert.details.total_children} children
                            </span>
                            <span className="px-2 py-0.5 bg-white rounded-full border">
                              {alert.details.scheduled_staff}/{alert.details.required_staff} staff
                            </span>
                            <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full">
                              Need {alert.details.staff_needed} more
                            </span>
                          </div>
                        )}
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatTime(alert.created_at)}
                          </span>
                          <div className="flex items-center gap-1">
                            {!alert.is_read && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 text-xs px-2"
                                onClick={() => markAsRead(alert.id)}
                              >
                                Mark read
                              </Button>
                            )}
                            {isAdmin && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 text-xs px-2 text-green-600 hover:text-green-700"
                                onClick={() => resolveAlert(alert.id)}
                                data-testid={`resolve-alert-${alert.id}`}
                              >
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Resolve
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center text-muted-foreground">
                <CheckCircle className="w-12 h-12 mx-auto mb-2 text-green-500 opacity-50" />
                <p className="font-medium">All Clear!</p>
                <p className="text-sm">No active ratio alerts</p>
              </div>
            )}
          </ScrollArea>

          {/* Footer */}
          <div className="px-4 py-2 border-t bg-muted/20">
            <Button
              variant="ghost"
              className="w-full justify-center text-sm text-primary hover:text-primary"
              onClick={() => {
                setIsOpen(false);
                window.location.href = '/staff/schedule';
              }}
            >
              View Schedule
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
